import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Send, 
  Mail, 
  Users, 
  Filter, 
  Eye, 
  Calendar, 
  DollarSign, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  FileText,
  Loader2,
  ChevronRight,
  History,
  Menu,
  FileStack,
  UserMinus
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EmailCampaignsPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('compose');
  
  // Filter state
  const [filters, setFilters] = useState({
    booking_status: [],
    has_outstanding_balance: false,
    min_outstanding: '',
    event_date_from: '',
    event_date_to: '',
    event_types: [],
    include_leads: false,
    exclude_previous_recipients: false,
    exclude_template_id: '',
    exclude_campaign_id: ''
  });
  
  // Campaign state
  const [campaignName, setCampaignName] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTemplateName, setSelectedTemplateName] = useState('');
  
  // Data state
  const [eventTypes, setEventTypes] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [previewRecipients, setPreviewRecipients] = useState([]);
  const [previewCount, setPreviewCount] = useState(0);
  const [excludedCount, setExcludedCount] = useState(0);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showCampaignDetailDialog, setShowCampaignDetailDialog] = useState(false);

  const bookingStatusOptions = [
    { value: 'pending', label: 'Pending', color: 'bg-yellow-500' },
    { value: 'confirmed', label: 'Confirmed', color: 'bg-green-500' },
    { value: 'completed', label: 'Completed', color: 'bg-blue-500' },
    { value: 'cancelled', label: 'Cancelled', color: 'bg-red-500' }
  ];

  const templateVariables = [
    { var: '{client_name}', desc: 'Client\'s name' },
    { var: '{event_date}', desc: 'Event date' },
    { var: '{event_type}', desc: 'Event type' },
    { var: '{outstanding_balance}', desc: 'Outstanding balance amount' },
    { var: '{company_name}', desc: 'Your company name' }
  ];

  useEffect(() => {
    fetchEventTypes();
    fetchCampaigns();
    fetchEmailTemplates();
  }, []);

  const fetchEventTypes = async () => {
    try {
      const response = await axios.get(`${API}/event-types`);
      setEventTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch event types:', error);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await axios.get(`${API}/email-templates`);
      setEmailTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch email templates:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/email/campaigns`);
      setCampaigns(response.data || []);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (templateId) => {
    if (templateId === 'none') {
      setSelectedTemplateId('');
      setSelectedTemplateName('');
      return;
    }
    const template = emailTemplates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplateId(template.id);
      setSelectedTemplateName(template.name);
      setSubject(template.subject || '');
      setContent(template.body || '');
      toast.success(`Template "${template.name}" loaded`);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const filterData = {
        ...filters,
        booking_status: filters.booking_status.length > 0 ? filters.booking_status : null,
        min_outstanding: filters.min_outstanding ? parseFloat(filters.min_outstanding) : null,
        event_types: filters.event_types.length > 0 ? filters.event_types : null,
        event_date_from: filters.event_date_from || null,
        event_date_to: filters.event_date_to || null,
        exclude_template_id: filters.exclude_template_id || null,
        exclude_campaign_id: filters.exclude_campaign_id || null
      };
      
      const response = await axios.post(`${API}/email/campaign/preview`, filterData);
      setPreviewRecipients(response.data.recipients || []);
      setPreviewCount(response.data.count || 0);
      setExcludedCount(response.data.excluded_count || 0);
      setShowPreviewDialog(true);
    } catch (error) {
      console.error('Preview failed:', error);
      toast.error('Failed to preview recipients');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSendCampaign = async () => {
    if (!subject.trim()) {
      toast.error('Please enter an email subject');
      return;
    }
    if (!content.trim()) {
      toast.error('Please enter email content');
      return;
    }
    if (previewCount === 0) {
      toast.error('No recipients match your filters');
      return;
    }

    setSending(true);
    try {
      const filterData = {
        booking_status: filters.booking_status.length > 0 ? filters.booking_status : null,
        min_outstanding: filters.min_outstanding ? parseFloat(filters.min_outstanding) : null,
        event_types: filters.event_types.length > 0 ? filters.event_types : null,
        event_date_from: filters.event_date_from || null,
        event_date_to: filters.event_date_to || null,
        has_outstanding_balance: filters.has_outstanding_balance,
        include_leads: filters.include_leads,
        exclude_previous_recipients: filters.exclude_previous_recipients,
        exclude_template_id: filters.exclude_template_id || null,
        exclude_campaign_id: filters.exclude_campaign_id || null
      };

      const response = await axios.post(`${API}/email/campaign/send`, {
        filter: filterData,
        subject,
        content,
        campaign_name: campaignName || null,
        template_id: selectedTemplateId || null,
        template_name: selectedTemplateName || null
      });

      toast.success(`Campaign sent! ${response.data.sent} emails delivered, ${response.data.failed} failed`);
      
      // Reset form
      setCampaignName('');
      setSubject('');
      setContent('');
      setSelectedTemplateId('');
      setSelectedTemplateName('');
      setPreviewRecipients([]);
      setPreviewCount(0);
      setExcludedCount(0);
      setShowPreviewDialog(false);
      
      // Refresh campaigns list
      fetchCampaigns();
      setActiveTab('history');
    } catch (error) {
      console.error('Send campaign failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to send campaign');
    } finally {
      setSending(false);
    }
  };

  const handleViewCampaign = async (campaignId) => {
    try {
      const response = await axios.get(`${API}/email/campaigns/${campaignId}`);
      setSelectedCampaign(response.data);
      setShowCampaignDetailDialog(true);
    } catch (error) {
      console.error('Failed to fetch campaign details:', error);
      toast.error('Failed to load campaign details');
    }
  };

  const toggleBookingStatus = (status) => {
    setFilters(prev => ({
      ...prev,
      booking_status: prev.booking_status.includes(status)
        ? prev.booking_status.filter(s => s !== status)
        : [...prev.booking_status, status]
    }));
  };

  const toggleEventType = (eventType) => {
    setFilters(prev => ({
      ...prev,
      event_types: prev.event_types.includes(eventType)
        ? prev.event_types.filter(et => et !== eventType)
        : [...prev.event_types, eventType]
    }));
  };

  const insertVariable = (variable) => {
    setContent(prev => prev + variable);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      sending: { color: 'bg-yellow-500', icon: Loader2, text: 'Sending' },
      completed: { color: 'bg-green-500', icon: CheckCircle2, text: 'Completed' },
      completed_with_errors: { color: 'bg-orange-500', icon: XCircle, text: 'Completed with Errors' }
    };
    const config = statusConfig[status] || { color: 'bg-gray-500', icon: Clock, text: status };
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white`}>
        <Icon className={`w-3 h-3 mr-1 ${status === 'sending' ? 'animate-spin' : ''}`} />
        {config.text}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="email-campaigns-page">
      {/* Mobile sidebar toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 lg:hidden"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-toggle"
      >
        <Menu className="h-6 w-6" />
      </Button>

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-40 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:relative lg:translate-x-0 transition-transform duration-200`}>
        <Sidebar 
          user={user} 
          onLogout={onLogout} 
          isEmployee={isEmployee} 
          permissions={permissions} 
          employeeInfo={employeeInfo}
          onNavigate={() => setSidebarOpen(false)}
        />
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 p-4 lg:p-8 overflow-auto">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold font-heading text-foreground flex items-center gap-3" data-testid="page-title">
              <Mail className="w-8 h-8 text-neon-blue" />
              Email Campaigns
            </h1>
            <p className="text-muted-foreground mt-2">Send targeted emails to your clients based on booking criteria</p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="glass-card border border-glass-border">
              <TabsTrigger value="compose" className="data-[state=active]:bg-neon-blue/20" data-testid="tab-compose">
                <Send className="w-4 h-4 mr-2" />
                Compose
              </TabsTrigger>
              <TabsTrigger value="history" className="data-[state=active]:bg-neon-blue/20" data-testid="tab-history">
                <History className="w-4 h-4 mr-2" />
                History
              </TabsTrigger>
            </TabsList>

            {/* Compose Tab */}
            <TabsContent value="compose" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Filters Section */}
                <Card className="glass-card border-glass-border lg:col-span-1">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Filter className="w-5 h-5 text-neon-purple" />
                      Filter Recipients
                    </CardTitle>
                    <CardDescription>Select criteria to target specific clients</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Booking Status */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">Booking Status</Label>
                      <div className="space-y-2">
                        {bookingStatusOptions.map(option => (
                          <div key={option.value} className="flex items-center space-x-2">
                            <Checkbox
                              id={`status-${option.value}`}
                              checked={filters.booking_status.includes(option.value)}
                              onCheckedChange={() => toggleBookingStatus(option.value)}
                              data-testid={`filter-status-${option.value}`}
                            />
                            <Label htmlFor={`status-${option.value}`} className="flex items-center gap-2 cursor-pointer">
                              <span className={`w-2 h-2 rounded-full ${option.color}`}></span>
                              {option.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Outstanding Balance */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-green-500" />
                        Outstanding Balance
                      </Label>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="has-outstanding"
                          checked={filters.has_outstanding_balance}
                          onCheckedChange={(checked) => setFilters(prev => ({ ...prev, has_outstanding_balance: checked }))}
                          data-testid="filter-has-outstanding"
                        />
                        <Label htmlFor="has-outstanding" className="cursor-pointer">Has outstanding balance</Label>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="min-outstanding" className="text-xs text-muted-foreground">Minimum amount</Label>
                        <Input
                          id="min-outstanding"
                          type="number"
                          placeholder="e.g., 100"
                          value={filters.min_outstanding}
                          onChange={(e) => setFilters(prev => ({ ...prev, min_outstanding: e.target.value }))}
                          className="bg-background/50"
                          data-testid="filter-min-outstanding"
                        />
                      </div>
                    </div>

                    {/* Event Date Range */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-neon-blue" />
                        Event Date Range
                      </Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label htmlFor="date-from" className="text-xs text-muted-foreground">From</Label>
                          <Input
                            id="date-from"
                            type="date"
                            value={filters.event_date_from}
                            onChange={(e) => setFilters(prev => ({ ...prev, event_date_from: e.target.value }))}
                            className="bg-background/50"
                            data-testid="filter-date-from"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="date-to" className="text-xs text-muted-foreground">To</Label>
                          <Input
                            id="date-to"
                            type="date"
                            value={filters.event_date_to}
                            onChange={(e) => setFilters(prev => ({ ...prev, event_date_to: e.target.value }))}
                            className="bg-background/50"
                            data-testid="filter-date-to"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Event Types */}
                    {eventTypes.length > 0 && (
                      <div className="space-y-3">
                        <Label className="text-sm font-medium">Event Types</Label>
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {eventTypes.map(et => (
                            <div key={et.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`event-type-${et.id}`}
                                checked={filters.event_types.includes(et.name)}
                                onCheckedChange={() => toggleEventType(et.name)}
                                data-testid={`filter-event-type-${et.id}`}
                              />
                              <Label htmlFor={`event-type-${et.id}`} className="cursor-pointer">{et.name}</Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Include Leads */}
                    <div className="flex items-center space-x-2 pt-2 border-t border-glass-border">
                      <Checkbox
                        id="include-leads"
                        checked={filters.include_leads}
                        onCheckedChange={(checked) => setFilters(prev => ({ ...prev, include_leads: checked }))}
                        data-testid="filter-include-leads"
                      />
                      <Label htmlFor="include-leads" className="cursor-pointer">Include leads (non-booked contacts)</Label>
                    </div>

                    {/* Exclusion Options */}
                    <div className="space-y-3 pt-2 border-t border-glass-border">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <UserMinus className="w-4 h-4 text-orange-500" />
                        Exclude Previous Recipients
                      </Label>
                      
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="exclude-all-previous"
                          checked={filters.exclude_previous_recipients}
                          onCheckedChange={(checked) => setFilters(prev => ({ 
                            ...prev, 
                            exclude_previous_recipients: checked,
                            exclude_template_id: checked ? '' : prev.exclude_template_id,
                            exclude_campaign_id: checked ? '' : prev.exclude_campaign_id
                          }))}
                          data-testid="filter-exclude-previous"
                        />
                        <Label htmlFor="exclude-all-previous" className="cursor-pointer text-sm">
                          Exclude all previous campaign recipients
                        </Label>
                      </div>

                      {campaigns.length > 0 && !filters.exclude_previous_recipients && (
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Or exclude recipients of specific campaign</Label>
                          <Select 
                            value={filters.exclude_campaign_id || 'none'} 
                            onValueChange={(val) => setFilters(prev => ({ 
                              ...prev, 
                              exclude_campaign_id: val === 'none' ? '' : val,
                              exclude_template_id: ''
                            }))}
                          >
                            <SelectTrigger className="bg-background/50" data-testid="exclude-campaign-select">
                              <SelectValue placeholder="Select a campaign to exclude" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No exclusion</SelectItem>
                              {campaigns.map(c => (
                                <SelectItem key={c.id} value={c.id}>
                                  {c.name} ({c.sent_count} sent)
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {emailTemplates.length > 0 && !filters.exclude_previous_recipients && !filters.exclude_campaign_id && (
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Or exclude recipients of template</Label>
                          <Select 
                            value={filters.exclude_template_id || 'none'} 
                            onValueChange={(val) => setFilters(prev => ({ 
                              ...prev, 
                              exclude_template_id: val === 'none' ? '' : val 
                            }))}
                          >
                            <SelectTrigger className="bg-background/50" data-testid="exclude-template-select">
                              <SelectValue placeholder="Select a template to exclude" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No exclusion</SelectItem>
                              {emailTemplates.map(t => (
                                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    {/* Preview Button */}
                    <Button 
                      onClick={handlePreview} 
                      disabled={previewing}
                      className="w-full bg-neon-purple hover:bg-neon-purple/80"
                      data-testid="preview-recipients-btn"
                    >
                      {previewing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Eye className="w-4 h-4 mr-2" />
                      )}
                      Preview Recipients ({previewCount})
                    </Button>
                    
                    {excludedCount > 0 && (
                      <p className="text-xs text-orange-500 text-center">
                        {excludedCount} recipients excluded from previous campaigns
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Email Composer Section */}
                <Card className="glass-card border-glass-border lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-neon-blue" />
                      Compose Email
                    </CardTitle>
                    <CardDescription>Create your email campaign message</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Template Selector */}
                    {emailTemplates.length > 0 && (
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <FileStack className="w-4 h-4 text-neon-purple" />
                          Use Email Template
                        </Label>
                        <Select 
                          value={selectedTemplateId || 'none'} 
                          onValueChange={handleSelectTemplate}
                        >
                          <SelectTrigger className="bg-background/50" data-testid="template-select">
                            <SelectValue placeholder="Select a template to start with..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Start from scratch</SelectItem>
                            {emailTemplates.map(t => (
                              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {selectedTemplateId && (
                          <p className="text-xs text-muted-foreground">
                            Using template: <span className="text-neon-purple">{selectedTemplateName}</span>
                          </p>
                        )}
                      </div>
                    )}

                    {/* Campaign Name */}
                    <div className="space-y-2">
                      <Label htmlFor="campaign-name">Campaign Name (optional)</Label>
                      <Input
                        id="campaign-name"
                        placeholder="e.g., December Follow-up"
                        value={campaignName}
                        onChange={(e) => setCampaignName(e.target.value)}
                        className="bg-background/50"
                        data-testid="campaign-name-input"
                      />
                    </div>

                    {/* Subject */}
                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject Line *</Label>
                      <Input
                        id="subject"
                        placeholder="e.g., Reminder: Your upcoming event on {event_date}"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        className="bg-background/50"
                        data-testid="subject-input"
                      />
                    </div>

                    {/* Template Variables */}
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Available Variables (click to insert)</Label>
                      <div className="flex flex-wrap gap-2">
                        {templateVariables.map(tv => (
                          <Button
                            key={tv.var}
                            variant="outline"
                            size="sm"
                            onClick={() => insertVariable(tv.var)}
                            className="text-xs h-7"
                            title={tv.desc}
                            data-testid={`var-${tv.var.replace(/[{}]/g, '')}`}
                          >
                            {tv.var}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="space-y-2">
                      <Label htmlFor="content">Email Content * (HTML supported)</Label>
                      <textarea
                        id="content"
                        className="w-full h-64 p-3 rounded-lg bg-background/50 border border-glass-border focus:border-neon-blue focus:ring-1 focus:ring-neon-blue outline-none resize-none font-mono text-sm"
                        placeholder={`Dear {client_name},

We hope you're excited about your upcoming {event_type} on {event_date}!

If you have any questions or need to make changes, please don't hesitate to reach out.

Best regards,
{company_name}`}
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        data-testid="content-textarea"
                      />
                    </div>

                    {/* Send Button */}
                    <div className="flex items-center justify-between pt-4 border-t border-glass-border">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="w-4 h-4" />
                        {previewCount > 0 ? (
                          <span>{previewCount} recipients selected</span>
                        ) : (
                          <span>Click "Preview Recipients" to see matches</span>
                        )}
                      </div>
                      <Button
                        onClick={handleSendCampaign}
                        disabled={sending || previewCount === 0}
                        className="bg-neon-blue hover:bg-neon-blue/80"
                        data-testid="send-campaign-btn"
                      >
                        {sending ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Send Campaign
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="history" className="space-y-4">
              <Card className="glass-card border-glass-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5 text-neon-purple" />
                    Campaign History
                  </CardTitle>
                  <CardDescription>View past email campaigns and their results</CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-neon-blue" />
                    </div>
                  ) : campaigns.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No campaigns sent yet</p>
                      <p className="text-sm">Your campaign history will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {campaigns.map(campaign => (
                        <div
                          key={campaign.id}
                          className="flex items-center justify-between p-4 rounded-lg bg-background/50 border border-glass-border hover:border-neon-blue/50 transition-colors cursor-pointer"
                          onClick={() => handleViewCampaign(campaign.id)}
                          data-testid={`campaign-${campaign.id}`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                              <h3 className="font-medium">{campaign.name || 'Untitled Campaign'}</h3>
                              {getStatusBadge(campaign.status)}
                            </div>
                            <p className="text-sm text-muted-foreground">{campaign.subject}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {campaign.recipient_count} recipients
                              </span>
                              <span className="flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3 text-green-500" />
                                {campaign.sent_count} sent
                              </span>
                              {campaign.failed_count > 0 && (
                                <span className="flex items-center gap-1">
                                  <XCircle className="w-3 h-3 text-red-500" />
                                  {campaign.failed_count} failed
                                </span>
                              )}
                              {campaign.template_name && (
                                <span className="flex items-center gap-1">
                                  <FileStack className="w-3 h-3 text-neon-purple" />
                                  {campaign.template_name}
                                </span>
                              )}
                              <span>
                                {new Date(campaign.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Preview Recipients Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="glass-card border-glass-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-neon-blue" />
              Preview Recipients ({previewCount})
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-96">
            {previewRecipients.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No recipients match your filter criteria</p>
                <p className="text-sm">Try adjusting your filters</p>
              </div>
            ) : (
              <div className="space-y-2">
                {previewRecipients.map((recipient, index) => (
                  <div
                    key={`${recipient.email}-${index}`}
                    className="flex items-center justify-between p-3 rounded-lg bg-background/50 border border-glass-border"
                  >
                    <div>
                      <p className="font-medium">{recipient.name}</p>
                      <p className="text-sm text-muted-foreground">{recipient.email}</p>
                    </div>
                    <div className="text-right text-sm">
                      <Badge variant="outline" className="mb-1">
                        {recipient.type === 'lead' ? 'Lead' : recipient.status}
                      </Badge>
                      {recipient.outstanding > 0 && (
                        <p className="text-orange-500 font-medium">${recipient.outstanding.toFixed(2)} owed</p>
                      )}
                      {recipient.event_date && (
                        <p className="text-muted-foreground">{recipient.event_date}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          <div className="flex justify-end gap-2 pt-4 border-t border-glass-border">
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Close
            </Button>
            <Button
              onClick={handleSendCampaign}
              disabled={sending || previewCount === 0}
              className="bg-neon-blue hover:bg-neon-blue/80"
              data-testid="confirm-send-btn"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send to {previewCount} Recipients
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Campaign Detail Dialog */}
      <Dialog open={showCampaignDetailDialog} onOpenChange={setShowCampaignDetailDialog}>
        <DialogContent className="glass-card border-glass-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-neon-blue" />
              Campaign Details
            </DialogTitle>
          </DialogHeader>
          {selectedCampaign && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground text-xs">Name</Label>
                  <p className="font-medium">{selectedCampaign.name || 'Untitled'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Status</Label>
                  <div>{getStatusBadge(selectedCampaign.status)}</div>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Sent</Label>
                  <p className="font-medium text-green-500">{selectedCampaign.sent_count}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Failed</Label>
                  <p className="font-medium text-red-500">{selectedCampaign.failed_count}</p>
                </div>
                {selectedCampaign.template_name && (
                  <div className="col-span-2">
                    <Label className="text-muted-foreground text-xs">Template Used</Label>
                    <p className="font-medium flex items-center gap-2">
                      <FileStack className="w-4 h-4 text-neon-purple" />
                      {selectedCampaign.template_name}
                    </p>
                  </div>
                )}
                <div className="col-span-2">
                  <Label className="text-muted-foreground text-xs">Subject</Label>
                  <p className="font-medium">{selectedCampaign.subject}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground text-xs">Created</Label>
                  <p className="font-medium">{new Date(selectedCampaign.created_at).toLocaleString()}</p>
                </div>
              </div>

              {selectedCampaign.logs && selectedCampaign.logs.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs">Email Logs</Label>
                  <ScrollArea className="max-h-48">
                    <div className="space-y-1">
                      {selectedCampaign.logs.map((log, index) => (
                        <div
                          key={log.id || index}
                          className="flex items-center justify-between p-2 rounded bg-background/50 text-sm"
                        >
                          <span>{log.recipient_name} ({log.recipient_email})</span>
                          <Badge variant={log.status === 'sent' ? 'default' : 'destructive'}>
                            {log.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
